import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  // ImageList,
  // ImageListItem,
  DialogTitle,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArtworkStatusRibbon from "../components/BannerRibbon";
import type { Artwork, ArtworkImage } from "../types/artwork";
import api from "../api";
import { useLanguage } from "../context/LanguageContext";
import { useSiteTheme } from "../theme/ThemeContext";
import { getOptimizedCloudinaryUrl } from "../lib/artworkImages";
import SEO from "../components/SEO";

type GalleryImage = {
  url: string;
  type: "artwork";
  short_description: string;
  source: string;
  width?: number;
  height?: number;
};

const getFullImageUrl = (url?: string | null): string => {
  if (!url) {
    return "/images/placeholder.webp";
  }
  return url;
};

function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [galleryImageIndex, setGalleryImageIndex] = useState(0);

  // Get all available images from artwork and variants
  const getAllImages = (artwork: Artwork | null): GalleryImage[] => {
    const allImages: GalleryImage[] = [];

    if (!artwork) return allImages;

    if (artwork.images && artwork.images.length > 0) {
      artwork.images.forEach((image: ArtworkImage) => {
        allImages.push({
          ...image,
          type: "artwork",
          short_description: artwork.short_description[language] || "Artwork",
          source: "artwork",
        });
      });
    } else if (artwork.image_url) {
      allImages.push({
        url: artwork.image_url,
        type: "artwork",
        short_description: artwork.short_description[language] || "Artwork",
        source: "artwork",
      });
    }

    console.log("🖼️ All images collected:", allImages.length, "images");
    return allImages;
  };

  const { palette } = useSiteTheme();

  useEffect(() => {
    const fetchArtwork = async () => {
      setLoading(true);
      setError(null);

      if (!id) {
        setError("No artwork ID provided");
        setLoading(false);
        return;
      }

      try {
        const response = await api.get<Artwork>(`/artworks/${id}`);

        const mappedArtwork: Artwork = {
          ...response.data,
          images:
            response.data.images?.map((image: any) => ({
              url: image.image_url,
              public_id: image.public_id,
              width: image.width,
              height: image.height,
              thumbnail_url: image.thumbnail_url,
              large_url: image.large_url,
            })) ?? [],
        };

        setArtwork(mappedArtwork);
      } catch (error) {
        console.error("Failed to load artwork:", error);
        setError("Artwork not found");
        setArtwork(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArtwork();
  }, [id]);

  const allImages = artwork ? getAllImages(artwork) : [];
  const currentImage = allImages[selectedImageIndex] || null;

  // Set initial variant selection when artwork loads
  useEffect(() => {
    if (artwork) {
      const allImages = getAllImages(artwork);

      // Set initial image index
      if (allImages.length > 0) {
        setSelectedImageIndex(0);
      }
    }
  }, [artwork]);

  // Enhanced image selection that also updates variant selection
  const handleImageSelect = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleGalleryOpen = () => {
    setGalleryImageIndex(selectedImageIndex);
    setGalleryOpen(true);
  };

  const handleGalleryNext = () => {
    const newIndex = findNextImage(galleryImageIndex, 1);
    setGalleryImageIndex(newIndex);
  };

  const handleGalleryPrev = () => {
    const newIndex = findNextImage(galleryImageIndex, -1);
    setGalleryImageIndex(newIndex);
  };

  // Smart navigation that allows selecting any variant
  const findNextImage = (
    currentIndex: number,
    direction: number = 1,
  ): number => {
    const newIndex =
      (currentIndex + direction + allImages.length) % allImages.length;
    return newIndex;
  };

  const handleMainImageNext = () => {
    const newIndex = findNextImage(selectedImageIndex, 1);
    setSelectedImageIndex(newIndex);
  };

  const handleMainImagePrev = () => {
    const newIndex = findNextImage(selectedImageIndex, -1);
    setSelectedImageIndex(newIndex);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !artwork) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || "Artwork not found"}</Alert>
      </Container>
    );
  }

  const handleArtworkEnquiry = () => {
    navigate("/contact", {
      state: {
        enquiry: {
          type: "artwork",
          artworkId: artwork.id,
          catalogueNumber: artwork.catalogue_number,
          artworkTitle: artwork.title[language],
        },
      },
    });
  };

  const handleCommissionEnquiry = () => {
    navigate("/contact", {
      state: {
        enquiry: {
          type: "commission",
        },
      },
    });
  };

  const ribbonText = artwork.is_sold
    ? "SOLD"
    : Number(artwork.price) === 0
      ? "NOT FOR SALE"
      : null;

  const descriptionText =
    typeof artwork.description?.[language] === "string"
      ? artwork.description[language]
      : "";

  const mainImage =
    allImages.length > 0
      ? getFullImageUrl(allImages[0].url)
      : artwork.image_url || "";

  const finalPrice = Number(artwork.price) || 0;
  const artworkUrl = `/artworks/${artwork.id}`;

  const mainImageWidth = currentImage?.width ?? artwork.widthCm ?? undefined;
  const mainImageHeight = currentImage?.height ?? artwork.heightCm ?? undefined;

  const artworkStructuredData = {
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    name: artwork.title[language],
    description: descriptionText,
    image: allImages.map((img) => getFullImageUrl(img.url)),
    creator: {
      "@type": "Person",
      name: "Ionela Barbatei Socolovici",
    },
    brand: {
      "@type": "Brand",
      name: "Byzantine Art Studio",
    },
    ...(!artwork.is_sold && finalPrice > 0
      ? {
          offers: {
            "@type": "Offer",
            url: `https://byzantineartstudio.co.uk${artworkUrl}`,
            priceCurrency: "GBP",
            price: finalPrice.toFixed(2),
            itemCondition: "https://schema.org/NewCondition",
            seller: {
              "@type": "Organization",
              name: "Byzantine Art Studio",
            },
          },
        }
      : {}),
  };

  return (
    <>
      <SEO
        title={artwork.title[language]}
        description={
          artwork.short_description?.[language] ||
          descriptionText ||
          "Hand-painted Byzantine iconography."
        }
        image={mainImage || undefined}
        url={artworkUrl}
        type="article"
        keywords={`${artwork.title[language]}, Byzantine icon, hand-painted icon, Orthodox icon, Byzantine Art Studio`}
        structuredData={artworkStructuredData}
      />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, bgcolor: palette.paper }}>
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mb: 2 }}
            >
              Back
            </Button>
          </Box>

          <Grid container spacing={4}>
            {/* Artwork Images */}
            <Grid item xs={12} md={6}>
              <Box sx={{ position: "relative" }}>
                {/* Main Image */}
                <Box
                  sx={{
                    position: "relative",
                    cursor: allImages.length > 1 ? "pointer" : "default",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: `1px solid ${palette.controlBorder}`,
                    aspectRatio:
                      artwork.widthCm && artwork.heightCm
                        ? `${artwork.widthCm} / ${artwork.heightCm}`
                        : "4 / 3",
                  }}
                  onClick={allImages.length > 1 ? handleGalleryOpen : undefined}
                >
                  <img
                    src={getOptimizedCloudinaryUrl(
                      getFullImageUrl(currentImage?.url),
                      800,
                    )}
                    srcSet={`
                      ${getOptimizedCloudinaryUrl(getFullImageUrl(currentImage?.url), 400)} 400w,
                      ${getOptimizedCloudinaryUrl(getFullImageUrl(currentImage?.url), 600)} 600w,
                      ${getOptimizedCloudinaryUrl(getFullImageUrl(currentImage?.url), 800)} 800w
                    `}
                    sizes="(max-width: 900px) calc(100vw - 48px), 550px"
                    alt={artwork.title[language]}
                    loading="eager"
                    fetchPriority="high"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                  <ArtworkStatusRibbon
                    isSold={artwork.is_sold}
                    price={artwork.price}
                    variant="detail"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Artwork Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h1" gutterBottom>
                {artwork.title[language]}
              </Typography>

              <Box sx={{ mb: 3 }}>
                {Number(artwork.price) > 0 || artwork.is_sold ? (
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    £{(Number(artwork.price) || 0).toFixed(2)}
                  </Typography>
                ) : null}
              </Box>

              {(artwork.heightCm || artwork.widthCm || artwork.depthCm) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Dimensions:{" "}
                  {[artwork.heightCm, artwork.widthCm, artwork.depthCm]
                    .filter(
                      (dimension): dimension is number =>
                        dimension !== null &&
                        dimension !== undefined &&
                        Number(dimension) > 0,
                    )
                    .map((dimension) => Number(dimension))
                    .join(" × ")}{" "}
                  cm
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {descriptionText.trim() !== "" && (
                <ul style={{ marginTop: 0, marginBottom: 16, paddingLeft: 24 }}>
                  {descriptionText
                    .split(".")
                    .map((sentence) => sentence.trim())
                    .filter(Boolean)
                    .map((sentence, idx) => (
                      <li key={idx}>{sentence}.</li>
                    ))}
                </ul>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Catalogue No: {artwork.catalogue_number}
              </Typography>

              <Box sx={{ mt: 2 }}>
                {artwork.is_sold ? (
                  <Button variant="contained" size="large" disabled>
                    Sold
                  </Button>
                ) : Number(artwork.price) === 0 ? (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 3 }}
                    >
                      Not for Sale
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Would you like to commission a similar icon?
                    </Typography>

                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        mt: 2,
                        bgcolor: palette.primary,
                        color: "#fff",
                        "&:hover": {
                          bgcolor: palette.secondary,
                        },
                      }}
                      onClick={handleCommissionEnquiry}
                    >
                      Enquire about a Commission
                    </Button>
                  </>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      mt: 2,
                      bgcolor: palette.primary,
                      color: "#fff",
                      "&:hover": {
                        bgcolor: palette.secondary,
                      },
                    }}
                    onClick={handleArtworkEnquiry}
                  >
                    Enquire about this Icon
                  </Button>
                )}
              </Box>

              {allImages.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "left",
                    gap: 1,
                    mt: 3,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    {language === "ro" ? "Alte imagini" : "Other views"}
                  </Typography>
                  {allImages.map((image, index) => (
                    <Box
                      key={`${image.url}-${index}`}
                      component="button"
                      type="button"
                      onClick={() => handleImageSelect(index)}
                      aria-label={`Show image ${index + 1}`}
                      sx={{
                        p: 0,
                        border: 2,
                        borderColor:
                          selectedImageIndex === index
                            ? "primary.main"
                            : "divider",
                        borderRadius: 1,
                        background: "none",
                        cursor: "pointer",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={getOptimizedCloudinaryUrl(
                          getFullImageUrl(image.url),
                          200,
                        )}
                        alt={`${artwork.title[language]} thumbnail ${index + 1}`}
                        sx={{
                          display: "block",
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Image Gallery Dialog */}
        <Dialog
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {artwork.title[language]} - Image {galleryImageIndex + 1} of{" "}
                {allImages.length}
              </Typography>
              <IconButton onClick={() => setGalleryOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ position: "relative", textAlign: "center" }}>
              {/* Navigation Buttons */}
              {allImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handleGalleryPrev}
                    sx={{
                      position: "absolute",
                      left: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      zIndex: 1,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleGalleryNext}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      zIndex: 1,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </>
              )}

              {/* Main Gallery Image */}
              <img
                src={getOptimizedCloudinaryUrl(
                  getFullImageUrl(currentImage?.url),
                  1600,
                )}
                alt={artwork.title[language]}
                loading="eager"
                fetchPriority="high"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
}

export default ArtworkDetail;
